import type { ComponentType } from 'react';
import type { GamePictureId } from './gamePictures';
import type { Season } from './season';

export type ThemeIconName =
  | 'social-participants'
  | 'social-games'
  | 'social-returning'
  | 'history'
  | 'heart'
  | 'cup'
  | 'home'
  | 'edit'
  | 'gift'
  | 'nav-main'
  | 'nav-exchange'
  | 'nav-messenger'
  | 'nav-organizer'
  | 'nav-settings'
  | 'nav-more'
  | 'link'
  | 'mail'
  | 'message'
  | 'header-gift'
  | 'bell'
  | 'user'
  | 'box';

export type DecorationVariant = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;
export const cardMotifs = ['forest', 'sparkles', 'gift', 'crystals', 'seasonal'] as const;
export type CardMotifVariant = (typeof cardMotifs)[number];
export interface CardDecorationProps {
  variant?: DecorationVariant;
  edges?: boolean;
  motif?: boolean;
}
export interface ParticleProps {
  contained?: boolean;
  testId?: string;
}
export type ArtworkRole = 'exchange' | 'menu' | 'menu-compact' | 'counter' | 'gift';
export type ArtworkSource = { kind: 'inline'; svg: string } | { kind: 'image'; src: string };
export interface ArtworkDefinition {
  source: ArtworkSource;
  width: number;
  height: number;
}
export interface LandscapeRasterImage {
  src: string;
  /** Optional art-directed sources, selected by the browser before downloading. */
  sources?: readonly { media: string; srcSet: string }[];
  /** Optional still SVG source for reduced motion and miniature previews. */
  stillSrc?: string;
  /** Trimmed image bounds as percentages of the unchanged native layer canvas. */
  bounds: readonly [number, number, number, number];
}
export interface LandscapeRaster {
  light: readonly LandscapeRasterImage[];
  dark: readonly LandscapeRasterImage[];
}
export interface LandscapeLayerDefinition {
  id: string;
  svg: string;
  raster?: LandscapeRaster;
  /** Duplicate the previous horizontal tile for seamless forward motion. */
  rasterRepeatX?: boolean;
  width: number;
  height: number;
  x: number;
  bottom: number;
  anchor: string;
  visibility: string;
  speed: number;
  theme?: 'day' | 'night';
  flipY?: boolean;
  slot?: 'counter';
}
/** Packages own visuals and placement; consumers own content, navigation and state. */
export interface SeasonPackage {
  id: Season;
  /** Exactly one active stylesheet, rendered on server and client. */
  css: string;
  /** Palette alone, for locally themed controls without loading scene styles. */
  colorsCss: string;
  landscape: { layers: readonly LandscapeLayerDefinition[] };
  artwork: Record<ArtworkRole, ArtworkDefinition>;
  gamePictures: Record<GamePictureId, string>;
  photoArtwork: string;
  /** Optional animal artwork by persisted participant picture ID; other themes keep stock art. */
  participantPictures?: Readonly<Partial<Record<string, string>>>;
  /** Optional semantic icon overrides; unspecified icons keep their default artwork. */
  icons?: Partial<Record<ThemeIconName, string>>;
  CardEdges: ComponentType<CardDecorationProps>;
  CardMotif: ComponentType<{ variant: CardMotifVariant }>;
  GameEmblemDecoration: ComponentType;
  CounterDecoration?: ComponentType;
  Particles: ComponentType<ParticleProps>;
  Continuation: ComponentType;
}

/** Shared deterministic card appearance; package components interpret each variant. */
export interface PickedCardAppearance {
  edges: DecorationVariant;
  decoration: CardMotifVariant;
}
